import { lazy } from 'react';
import { PiPlugBold } from 'react-icons/pi';
import { useLocation } from 'react-router-dom';

import {
  RiFolderVideoLine,
  RiApps2AiLine,
  RiHome5Line,
  RiFocus2Line,
  RiUserAddLine,
  RiChatSmile2Line
} from '@remixicon/react';

import { UserDetailsType } from '@/api/generated';
import { SUPPORT_URL } from '@/consts/consts';
import useLoginContext from '@/context/LoginFlowContextProvider/useLoginContext';
import RoutesMap from '@/enums/RoutesMap';
import UserRole from '@/enums/UserRoles';
import { useCustomNavigate } from '@/hooks/useCustomNavigate';
import useModal from '@/hooks/useModal/useModal';

const LazyUserModal = lazy(() => import('@/components/modals/UserModal/UserModal'));

export const useMenuItems = (isSmallScreen: boolean, hideSidebar: () => void) => {
  const { currentUser } = useLoginContext();
  const isAdmin =
    currentUser?.type === UserDetailsType.ceo || currentUser?.roleId === UserRole.ADMIN;
  const { showModal } = useModal();
  const navigate = useCustomNavigate();
  const location = useLocation();

  const activePath = location.pathname;

  const onAddUserClick = () => {
    const modal = showModal(LazyUserModal, {
      onClose: () => modal.hide(),
      onConfirm: () => {
        modal.hide();
      }
    });
  };

  const iconSize = isSmallScreen ? 20 : 16;
  const menuItems = [
    {
      label: 'Home',
      icon: <RiHome5Line size={iconSize} />,
      action: () => navigate(RoutesMap.Home),
      isVisibleOnDesktop: true,
      isVisibleOnMobile: true,
      path: RoutesMap.Home,
      isBeta: false
    },
    {
      label: 'Meetings Hub',
      icon: <RiFolderVideoLine size={iconSize} />,
      action: () => navigate(RoutesMap.MeetingHub),
      isVisibleOnDesktop: true,
      isVisibleOnMobile: true,
      path: RoutesMap.MeetingHub,
      isBeta: false
    },

    {
      label: "Jamie's Capabilities",
      icon: <RiApps2AiLine size={iconSize} />,
      action: () => navigate(RoutesMap.JamiesCapabilities),
      isVisibleOnDesktop: true,
      isVisibleOnMobile: false,
      path: RoutesMap.JamiesCapabilities,
      isBeta: true
    },
    {
      label: 'Strategic plan',
      icon: <RiFocus2Line size={iconSize} />,
      action: () => navigate(RoutesMap.Goals),
      isVisibleOnDesktop: true,
      isVisibleOnMobile: true,
      path: RoutesMap.Goals,
      isBeta: false
    },
    // TODO: add back in when integrations are mobile friendly
    // {
    //   label: 'Integrations',
    //   icon: <PiPlugBold size={iconSize} />,
    //   action: () => navigate(RoutesMap.Integrations),
    //   mobileOnly: true,
    //   path: RoutesMap.Integrations
    // },
    // ...(isAdmin
    //   ? [
    //       {
    //         label: 'Invite',
    //         icon: <RiUserAddLine size={iconSize} />,
    //         action: onAddUserClick, // TODO: this does not work on mobile, must fix router navigation - remove * from router and use query params for all modals
    //         mobileOnly: true,
    //         path: null
    //       }
    //     ]
    //   : []),
    {
      label: 'FAQs',
      icon: <RiChatSmile2Line size={iconSize} />,
      action: () => window.open(SUPPORT_URL, '_blank'),
      isVisibleOnDesktop: true,
      isVisibleOnMobile: true,
      path: null,
      isBeta: false
    }
  ];

  const handleClick = (action: () => void) => {
    action();
    if (isSmallScreen) {
      hideSidebar();
    }
  };

  const isActive = (path: string | null) => {
    if (path === null) return false;
    return activePath === path;
  };

  return { menuItems, handleClick, isActive };
};
